import {
  Component,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { Router } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import {
  CredentialVerification,
  ICredentialVerification,
} from "./../../../shared/models/products/credential-verification";
import { Address } from "./../../../shared/models/address";
import { RegexPatterns } from "./../../../../shared/regex/regex-patterns";
import { NotificationsService } from "./../../../../shared/services/notifications.service";
import { ViewStateService } from "./../../../../shared/services/view-state.service";
import { PurchaseStateService } from "./../../../shared/services/purchase-state.service";
import { CommonApiService } from "./../../../../shared/services/api/common-api.service";
import { UserService } from "./../../../../shared/services/user.service";
import { map, filter, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

@Component({
  selector: "app-third-party",
  templateUrl: "./third-party.component.html",
})
export class ThirdPartyComponent implements OnInit, OnDestroy {
  thirdPartyForm: UntypedFormGroup;
  product: ICredentialVerification;
  countries: any;
  zipLength = 5;
  manualValidForm = true;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private viewState: ViewStateService,
    private router: Router,
    private notifications: NotificationsService,
    private purchaseService: PurchaseStateService,
    private purchaseState: PurchaseStateService,
    private commonApiService: CommonApiService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.product = <CredentialVerification>(
      this.purchaseState.getTempProductData()
    );
    this.getCountries();
    this.buildForm();
  }

  isExpired(): boolean {
    if (
      this.userService.CurrentUser.hasExpiredCredentials &&
      !this.userService.CurrentUser.credentialHolder
    ) {
      return true;
    }
    return false;
  }

  onSubmit(updateFormValue) {
    this.formValues = updateFormValue;
    this.validateForm();
    if (this.thirdPartyForm.valid && this.isValidZip()) {
      let address = new Address();
      address.name = this.formValues.name;
      address.line1 = this.formValues.address1;
      address.line2 = this.formValues.address2;
      address.line3 = this.formValues.address3;
      address.city = this.formValues.city;
      address.state = this.formValues.state;
      address.postalCode = this.formValues.zip;
      address.countryId = this.formValues.countryId;
      const country = this.countries.filter(
        (p) => p.id == this.formValues.countryId
      );
      address.countryName = country[0].value;

      this.product.sendTo = address;
      this.purchaseState.patchProducts(this.product);

      this.router.navigate(["purchase/checkout"]);
    }
  }

  private getCountries() {
    this.commonApiService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.countries = res;
        },
        (error) => {
          console.log(error);
        }
      );
  }

  isValidZip() {
    if (this.manualValidForm) {
      return true;
    } else {
      this.formErrors.zip = "invalid zip";
      return false;
    }
  }

  performZipCodeLookup() {
    if (this.thirdPartyForm.value.countryId === 1) {
      const zip = this.thirdPartyForm.value.zip
        ? this.thirdPartyForm.value.zip.split("-")
        : "";
      if (!zip) return;

      this.commonApiService
        .lookupStateAndCityByZipCode(zip[0])
        .pipe(
          takeUntil(this.destroy$),
          map((resp) => resp),
          filter((res) => res.length !== 0)
        )
        .subscribe(
          (val) => {
            const value = val[0] || "";
            if (!value) {
              this.formErrors.zip = "invalid zip";
              this.manualValidForm = false;
              return;
            }
            this.manualValidForm = true;
            this.formErrors.zip = null;
            this.thirdPartyForm.patchValue({
              state: value.stateCode,
              city: value.city,
            });
          },
          (error) => {
            this.formErrors.zip = "invalid zip";
            this.manualValidForm = false;
            console.log(error);
          }
        );
    } else {
      this.manualValidForm = true;
      this.formErrors.zip = null;
    }
  }

  onBack(event: Event) {
    event.preventDefault();
    this.router.navigate([this.purchaseService.previousRouteUrl]);
  }

  private buildForm(): void {
    this.thirdPartyForm = this.formBuilder.group({
      name: [
        this.formValues.name,
        [Validators.required, Validators.maxLength(75)],
      ],
      address1: [
        this.formValues.address1,
        [Validators.required, Validators.maxLength(35)],
      ],
      address2: [this.formValues.address2, [Validators.maxLength(35)]],
      address3: [this.formValues.address3, [Validators.maxLength(35)]],
      city: [
        this.formValues.city,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      state: [this.formValues.state, [Validators.required]],
      zip: [
        this.formValues.zip,
        [
          Validators.required, //validating in api
          Validators.maxLength(10), //VALIDATING IN API
        ],
      ],
      countryId: [this.formValues.countryId, []],
    });
    this.thirdPartyForm.get("countryId").patchValue(1);
  }

  private validateForm() {
    for (const control in this.thirdPartyForm.controls) {
      this.thirdPartyForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.thirdPartyForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private formValues = {
    name: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
  };

  formErrors = {
    name: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
  };

  validationMessages = {
    name: {
      required: "Name of Institution or Address Line 1 is required.",
      maxlength:
        "Name of Institution or Address Line 1 must be max 75 characters.",
    },
    address1: {
      required: "Address Line 1 is required.",
      maxlength: "Address Line 1 must be max 35 characters.",
    },
    address2: {
      maxlength: "Address Line 2 must be max 35 characters.",
    },
    address3: {
      maxlength: "Address Line 3 must be max 35 characters.",
    },
    city: {
      required: "City is required.",
      maxlength: "City must be max 50 characters.",
      pattern: "City must be letters only.",
    },
    state: {
      required: "State is required.",
    },
    zip: {
      required: "Zip is required.",
      maxlength: "Zip must be max 10 characters.",
    },
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
